import { Link } from 'react-router-dom';
import LegalLayout, {
  LegalSection,
  LegalSubheading,
  LegalList,
} from '../components/LegalLayout';
import { BRAND } from '../lib/brand';

export default function Terms() {
  return (
    <LegalLayout
      title="Terms of Service"
      intro={`These Terms of Service ("Terms") form a binding agreement between you and ${BRAND.legalName} ("${BRAND.name}", "we", "us", or "our") and govern your access to and use of the ${BRAND.domain} website and any related services (the "Services"). By accessing or using the Services, you agree to be bound by these Terms.`}
    >
      <LegalSection heading="1. Agreement to Terms">
        <p>
          By accessing or using the Services, you confirm that you have read,
          understood, and agree to be bound by these Terms and our Privacy
          Policy. If you are entering into these Terms on behalf of a company or
          other legal entity, you represent that you have the authority to bind
          that entity. If you do not agree, you may not use the Services.
        </p>
      </LegalSection>

      <LegalSection heading="2. Description of Services">
        <p>
          {BRAND.name} provides AI-assisted social media marketing tooling
          including, without limitation: content creation and scheduling,
          campaign management, analytics, audience research, and paid-media
          execution across third-party platforms such as Meta, Google, TikTok,
          and LinkedIn. Features may be added, changed, or removed at our
          discretion.
        </p>
      </LegalSection>

      <LegalSection heading="3. Eligibility and Account Registration">
        <p>
          You must be at least 18 years old (or the age of majority in your
          jurisdiction) and capable of forming a binding contract to use the
          Services. You agree to provide accurate, current, and complete
          information when creating an account, to keep that information up to
          date, and to safeguard your credentials. You are responsible for all
          activity that occurs under your account.
        </p>
      </LegalSection>

      <LegalSection heading="4. Subscriptions, Billing, and Payment">
        <LegalSubheading>4.1 Plans and renewals</LegalSubheading>
        <p>
          Paid Services are offered on a subscription basis. Unless otherwise
          stated, subscriptions automatically renew at the end of each billing
          cycle at the then-current rate. You may cancel renewal at any time as
          described in our{' '}
          <Link
            to="/cancellation"
            className="text-foreground underline underline-offset-4 hover:opacity-80"
          >
            Cancellation &amp; Refund Policy
          </Link>
          .
        </p>

        <LegalSubheading>4.2 Fees and taxes</LegalSubheading>
        <p>
          You agree to pay all fees specified at sign-up. Fees are exclusive of
          taxes, levies, and duties, which are your responsibility. We may
          change pricing with reasonable advance notice; changes take effect on
          the next renewal.
        </p>

        <LegalSubheading>4.3 Failed payments</LegalSubheading>
        <p>
          If a payment fails, we may suspend or terminate your access until
          payment is resolved. You authorize us to retry charges to your
          payment method on file.
        </p>
      </LegalSection>

      <LegalSection heading="5. Acceptable Use">
        <p>You agree not to, and not to permit any third party to:</p>
        <LegalList
          items={[
            'Use the Services in violation of any law, regulation, or third-party right.',
            'Upload or transmit content that is illegal, harmful, defamatory, harassing, infringing, or fraudulent.',
            'Attempt to gain unauthorized access to the Services, related systems, or other users\u2019 accounts.',
            'Reverse engineer, decompile, or disassemble any part of the Services, except to the extent permitted by law.',
            'Use the Services to send spam, unsolicited communications, or to engage in deceptive marketing.',
            'Use any automated means (bots, scrapers, crawlers) to access the Services without our prior written consent.',
            'Interfere with or disrupt the integrity or performance of the Services.',
            'Use the Services in any manner that violates the terms of any third-party platform integrated with the Services (e.g., Meta Platform Terms, Google API Services User Data Policy).',
          ]}
        />
      </LegalSection>

      <LegalSection heading="6. Your Content and License">
        <p>
          You retain all rights to the content you submit to the Services
          ("Your Content"). You grant {BRAND.name} a worldwide, non-exclusive,
          royalty-free license to host, store, copy, transmit, display, and
          process Your Content solely to operate, maintain, and improve the
          Services for your benefit. You represent that you have all rights
          necessary to grant this license.
        </p>
      </LegalSection>

      <LegalSection heading="7. Intellectual Property">
        <p>
          {BRAND.name} and its licensors retain all right, title, and interest
          in and to the Services, including all software, content, trademarks,
          logos, and underlying technology. Except for the limited rights
          granted to you under these Terms, no rights are granted to you by
          implication, estoppel, or otherwise.
        </p>
      </LegalSection>

      <LegalSection heading="8. Third-Party Services">
        <p>
          The Services may integrate with or link to third-party services
          (e.g., advertising platforms, analytics, payment processors). Your
          use of those services is governed by their terms and privacy
          policies. We are not responsible for, and do not endorse, third-party
          services.
        </p>
      </LegalSection>

      <LegalSection heading="9. Disclaimers">
        <p>
          THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
          WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WITHOUT
          LIMITATION IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
          PARTICULAR PURPOSE, AND NON-INFRINGEMENT. {BRAND.name} DOES NOT
          WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR
          PRODUCE ANY SPECIFIC BUSINESS RESULTS.
        </p>
      </LegalSection>

      <LegalSection heading="10. Limitation of Liability">
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT WILL{' '}
          {BRAND.legalName.toUpperCase()} BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
          SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES, INCLUDING LOSS OF
          PROFITS, REVENUE, DATA, GOODWILL, OR BUSINESS, ARISING OUT OF OR
          RELATED TO YOUR USE OF THE SERVICES.
        </p>
        <p>
          OUR TOTAL CUMULATIVE LIABILITY ARISING OUT OF OR RELATED TO THESE
          TERMS OR THE SERVICES WILL NOT EXCEED THE GREATER OF (A) THE FEES
          YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING
          RISE TO THE LIABILITY OR (B) ONE HUNDRED U.S. DOLLARS (US $100).
        </p>
      </LegalSection>

      <LegalSection heading="11. Indemnification">
        <p>
          You agree to defend, indemnify, and hold harmless {BRAND.legalName},
          its affiliates, and their respective officers, directors, employees,
          and agents from and against any claims, damages, liabilities, costs,
          and expenses (including reasonable attorneys' fees) arising out of or
          related to (a) Your Content, (b) your use of the Services, or (c)
          your breach of these Terms.
        </p>
      </LegalSection>

      <LegalSection heading="12. Termination and Suspension">
        <p>
          We may suspend or terminate your access to the Services at any time
          for material breach of these Terms, suspected fraud, or to comply
          with law. You may terminate your account at any time. Upon
          termination, your right to use the Services ceases immediately;
          provisions intended by their nature to survive (e.g., IP, disclaimers,
          liability limits, indemnification, governing law) will survive.
        </p>
      </LegalSection>

      <LegalSection heading="13. Governing Law and Dispute Resolution">
        <p>
          These Terms are governed by the laws of the {BRAND.governingLaw},
          without regard to its conflict-of-laws principles. The parties will
          first attempt to resolve any dispute informally by contacting{' '}
          <a
            className="text-foreground underline underline-offset-4 hover:opacity-80"
            href={`mailto:${BRAND.email}`}
          >
            {BRAND.email}
          </a>
          . If we cannot resolve a dispute within 60 days, the dispute will be
          resolved by binding arbitration in Los Angeles County, California, on
          an individual basis (no class actions). Either party may bring a
          qualifying claim in small-claims court.
        </p>
      </LegalSection>

      <LegalSection heading="14. Changes to These Terms">
        <p>
          We may modify these Terms from time to time. If changes are material,
          we will provide notice (for example, by email or in-product). Your
          continued use of the Services after the effective date constitutes
          acceptance of the updated Terms.
        </p>
      </LegalSection>

      <LegalSection heading="15. Contact Us">
        <p>
          Questions about these Terms? Reach us at{' '}
          <a
            className="text-foreground underline underline-offset-4 hover:opacity-80"
            href={`mailto:${BRAND.email}`}
          >
            {BRAND.email}
          </a>{' '}
          or by mail at {BRAND.legalName}, {BRAND.address}.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
